/**
 * Form Field Component
 * Wrapper component cho mỗi field (label + input + error)
 */

import { Component, Input, OnInit, OnDestroy, TemplateRef, ContentChild, HostBinding } from '@angular/core';
import { FormGroup, AbstractControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FieldConfig } from '../../models/field-config.interface';
import { FormLayout, VisibilityCondition } from '../../models/form-schema.interface';
import { ValidationService } from '../../services/validation.service';

@Component({
    selector: 'app-form-field',
    templateUrl: './form-field.component.html',
    styleUrls: ['./form-field.component.scss']
})
export class FormFieldComponent implements OnInit, OnDestroy {
    @Input() field!: FieldConfig;
    @Input() form!: FormGroup;
    @Input() layout?: FormLayout;
    @Input() showRequiredMark: boolean = true;

    @Input() customFieldTemplate?: TemplateRef<any>;

    @HostBinding('class') get hostClasses() {
        return `col-md-${this.fieldColSpan}`;
    }

    isVisible: boolean = true;
    private destroy$ = new Subject<void>();

    constructor(private validationService: ValidationService) { }

    /**
     * Check if options is array (not function)
     */
    get isOptionsArray(): boolean {
        return Array.isArray(this.field.options);
    }

    /**
     * Handle checkbox group change
     */
    onCheckboxGroupChange(event: any, value: any): void {
        const control = this.control;
        if (!control) return;

        const currentValues: any[] = control.value || [];
        const isChecked = event.target.checked;

        if (isChecked) {
            // Add value
            control.setValue([...currentValues, value]);
        } else {
            // Remove value
            control.setValue(currentValues.filter(v => v !== value));
        }
        control.markAsTouched();
    }

    ngOnInit(): void {
        this.setupVisibility();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    get control(): AbstractControl | null {
        return this.form.get(this.field.key);
    }

    get isRequired(): boolean {
        return this.validationService.isRequired(this.field);
    }

    get labelPosition(): string {
        return this.layout?.labelPosition || 'top';
    }

    get fieldColSpan(): number {
        const colSpan = this.field.layout?.colSpan || 12;
        const layoutColumns = this.layout?.columns || 1;

        // Cho phép full width (colSpan 12) bất kể số columns
        if (colSpan === 12) {
            return 12;
        }

        // Tính toán col span dựa trên số columns
        if (layoutColumns === 2) {
            return colSpan <= 6 ? colSpan : 6;
        }
        if (layoutColumns === 3) {
            return colSpan <= 4 ? colSpan : 4;
        }
        if (layoutColumns === 4) {
            return colSpan <= 3 ? colSpan : 3;
        }

        return colSpan;
    }


    get fieldClasses(): string {
        const classes: string[] = ['form-field', 'col-md-12'];

        // Error class
        if (this.control?.invalid && this.control?.touched) {
            classes.push('has-error');
        }

        // Custom class
        if (this.field.cssClass) {
            classes.push(this.field.cssClass);
        }

        // Label position
        classes.push(`label-${this.labelPosition}`);

        return classes.join(' ');
    }

    /**
     * Setup conditional visibility
     */
    private setupVisibility(): void {
        const visibleConfig = this.field.visible;

        // Default visible
        if (visibleConfig === undefined || visibleConfig === true) {
            this.isVisible = true;
            return;
        }

        if (visibleConfig === false) {
            this.isVisible = false;
            return;
        }

        // Visibility condition
        if (typeof visibleConfig === 'object') {
            this.setupVisibilityCondition(visibleConfig as VisibilityCondition);
        }
    }

    private setupVisibilityCondition(condition: VisibilityCondition): void {
        const sourceControl = this.form.get(condition.field);

        if (!sourceControl) {
            this.isVisible = true;
            return;
        }

        // Initial check
        this.isVisible = this.checkCondition(condition, sourceControl.value);

        // Watch for changes
        sourceControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => {
                this.isVisible = this.checkCondition(condition, value);
            });
    }

    private checkCondition(condition: VisibilityCondition, value: any): boolean {
        switch (condition.operator) {
            case 'equals':
                return value === condition.value;
            case 'notEquals':
                return value !== condition.value;
            case 'contains':
                return String(value).includes(String(condition.value));
            case 'greaterThan':
                return Number(value) > Number(condition.value);
            case 'lessThan':
                return Number(value) < Number(condition.value);
            case 'isEmpty':
                return !value || value === '';
            case 'isNotEmpty':
                return value && value !== '';
            default:
                return true;
        }
    }
}
